import React, { memo } from "react";
import LinkPreview from "./LinkPreview";
import Code from "./Code";
import remarkGfm from "remark-gfm";
import ReactMarkdown from "react-markdown";
import { safeUrl } from "utils/safeHtml";

type MarkdownViewerProps = {
  uuid: string;
  handleApplyAi?: (model: string) => Promise<void>;
  message: string;
  /**
   * `true` while the AI is still writing the response (streaming or
   * post-stream parsing). Downstream components use this to disable
   * interactive controls (e.g. the BPMN "apply" action) so the user
   * can't fire them against a half-finished model — that was causing
   * the backend to reject the apply with the original/incomplete
   * XML.
   */
  isStreaming?: boolean;
  /**
   * Whether the current user is an administrator of the selected
   * organization. When `false`, the BPMN preview's "Azioni" / "Apply"
   * controls are disabled.
   */
  isAdmin?: boolean;
};

const MarkdownViewer: React.FC<MarkdownViewerProps> = ({
  uuid,
  handleApplyAi,
  message,
  isStreaming = false,
  isAdmin,
}) => {
  return (
    <ReactMarkdown
      key={"markdown" + uuid}
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ href, children, ...rest }) => {
          const safeHref = safeUrl(href);
          if (!safeHref) return <span>{children}</span>;
          return (
            <LinkPreview
              key={`link-preview-${uuid}`}
              {...rest}
              href={safeHref}
            >
              {children}
            </LinkPreview>
          );
        },
        code: (props) => (
          <Code
            {...props}
            uuid={uuid}
            onApply={handleApplyAi}
            isStreaming={isStreaming}
            isAdmin={isAdmin}
          />
        ),
      }}
    >
      {message}
    </ReactMarkdown>
  );
};

export default memo(MarkdownViewer);
